import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import {
  TextField,
  Box,
  Typography,
  InputAdornment,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { showToast } from "../toastNotification";
import { useTranslation } from "react-i18next";
import { CONTROL_HEIGHT, CONTROL_RADIUS, NJIM } from "../../../../theme/njimTheme";

interface InputTextFieldProps {
  type?: string;
  name: string;
  placeholder: string;
  value: string | number | File | null | undefined;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  label?: string;
  endAdornment?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  rows?: number;
  backgroundColor?: string;
  readOnly?: boolean;
  maxLength?: number;
  infoTable?: ReactNode;
  marginBottom?: string;
  showCharCount?: boolean;
  startIcon?: ReactNode;
  autoComplete?: string;
}

const CustomInput: React.FC<InputTextFieldProps> = ({
  type,
  name,
  label,
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder,
  endAdornment,
  required,
  disabled,
  error,
  rows,
  backgroundColor,
  readOnly,
  maxLength,
  infoTable,
  showCharCount,
  marginBottom = "8px",
  startIcon,
  autoComplete,
}) => {
  const { t } = useTranslation();
  const hasShownToast = useRef(false);

  useEffect(() => {
    const currentLength = value?.toString()?.length || 0;

    if (showCharCount && maxLength && currentLength === maxLength) {
      if (!hasShownToast.current) {
        showToast(
          `${t("errors.onlyCharactersAllowed")} ${maxLength} ${t("errors.charactersAllowed")}`,
          "error"
        );

        hasShownToast.current = true;
      }
    } else {
      hasShownToast.current = false;
    }
  }, [value, maxLength, showCharCount, t]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        marginBottom: error ? "0px" : marginBottom,
      }}
    >
      {label && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            fontSize: "14px",
            display: "flex",
            color: NJIM.textPrimary,
          }}
        >
          {label}{" "}
          {required && (
            <span
              style={{ color: NJIM.danger, marginInlineStart: "5px", lineHeight: "1.5" }}
            >
              *
            </span>
          )}
          {infoTable && (
            <Tooltip
              title={infoTable}
              placement="right"
              enterTouchDelay={0}
              leaveTouchDelay={4000}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: NJIM.white,
                    color: NJIM.textPrimary,
                    boxShadow: 2,
                    p: 1,
                    maxWidth: 350,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{ marginInlineStart: 0.5, color: NJIM.textSecondary, padding: "0px" }}
                tabIndex={-1}
                aria-label="Info"
              >
                <InfoOutlinedIcon fontSize="small" sx={{ padding: "0px" }} />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      )}
      <TextField
        autoComplete={autoComplete ?? "off"}
        fullWidth
        variant="outlined"
        type={type}
        name={name}
        error={!!error}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        multiline={rows ? true : false}
        rows={rows}
        onKeyDown={(e) => {
          if (type === "number") {
            if (
              e.key === "-" ||
              e.key === "+" ||
              e.key === "e" ||
              e.key === "E" ||
              e.key === "."
            ) {
              e.preventDefault();
            }
          }
          onKeyDown?.(e);
        }}
        onWheel={(e) => {
          if (type === "number") {
            (e.target as HTMLInputElement).blur();
          }
        }}
        inputProps={{
          readOnly,
          maxLength: typeof maxLength === "number" ? maxLength : 500,
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            padding: "8px",
            height: rows ? "auto" : CONTROL_HEIGHT,
            fontSize: "14px",
            color: NJIM.textPrimary,
            fontWeight: "normal",
            border: "none",
          },
          "& .MuiInputBase-input": {
            color: NJIM.textPrimary,
            zIndex: 10,
            "&::placeholder": {
              color: NJIM.textMuted,
              opacity: 1,
              zIndex: 10,
            },
          },
          "& .MuiInputBase-input::placeholder": {
            color: NJIM.textMuted,
            opacity: 1,
          },
          "& .Mui-error": {
            color: NJIM.danger,
          },
          "& fieldset": {
            border: `1px solid ${NJIM.border}`,
            background: backgroundColor || NJIM.white,
            borderRadius: CONTROL_RADIUS,
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: `${NJIM.textSecondary} !important`,
          },
          "&:hover fieldset, &.Mui-focused fieldset": {
            borderRadius: CONTROL_RADIUS,
            border: `2px solid ${NJIM.mint} !important`,
          },
        }}
        InputProps={{
          startAdornment: startIcon ? (
            <InputAdornment
              position="start"
              sx={{
                margin: 0,
                paddingInlineEnd: "8px",
              }}
            >
              {startIcon}
            </InputAdornment>
          ) : null,
          endAdornment: endAdornment ? (
            <InputAdornment
              position="end"
              sx={{
                margin: 0,
                "& .MuiIconButton-root": {
                  margin: 0,
                },
              }}
            >
              {endAdornment}
            </InputAdornment>
          ) : null,
        }}
      />
      {(error || showCharCount) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {error && (
            <Typography
              className="f-12"
              sx={{
                fontWeight: "medium",
                color: NJIM.danger,
                whiteSpace: "nowrap",
              }}
            >
              {error || ""}
            </Typography>
          )}
          {showCharCount && (
            <Typography
              className="f-12"
              sx={{
                fontWeight: "medium",
                color: NJIM.textSecondary,
                whiteSpace: "nowrap",
                marginInlineStart: "auto",
              }}
            >
              {`${value?.toString()?.length || 0}/${maxLength || 500} characters`}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CustomInput;
